import type { AutocompleteData } from "@color-finder/color-finder-api/models";
import type { ColorFilterValue } from "components/filter/color-filter/ColorFilter";
import {
  OEM_COLOR_CODE_SYSTEM,
  type OemColorValue,
  oemColorTitle,
} from "components/filter/color-filter/oem-color";
import {
  getColorEffectValue,
  getColorGroupValue,
  getColorStatusValue,
  getFakeAutocompleteValue,
  getOpacityValue,
} from "service/autocomplete-lookup";
import type { ColorCieLab } from "service/color-math";
import type { FilterEntry, FilterType } from "./filters";

export interface FilterConfig<T = any> {
  type: string;
  toUrl: (filter: T) => Record<string, string | string[]>;
  fromUrl: (params: URLSearchParams) => FilterEntry<T> | undefined;
  title: (filter: T) => string;
}

interface AutocompleteConfigProps {
  type: FilterType;
  urlParamName: string;
  lookupFunction: (v: string) => AutocompleteData | undefined;
}

const buildAutocompleteConfig: (
  props: AutocompleteConfigProps,
) => FilterConfig<Array<AutocompleteData>> = ({
  type,
  urlParamName,
  lookupFunction,
}) => ({
  type: type,
  toUrl: (value) => ({ [urlParamName]: value.map((v) => v.value) }),
  fromUrl: (params) =>
    params.has(urlParamName)
      ? {
          type: type,
          value: params
            .getAll(urlParamName)
            .map((v) => lookupFunction(v))
            .filter((v) => v)
            .map((v) => v!),
        }
      : undefined,
  title: (filter) => filter.map((v) => v.name).join(", "),
});

function buildBooleanConfig(
  type: FilterType,
  urlParamName: string = type,
  title: string,
): FilterConfig<boolean | undefined> {
  return {
    type: type,
    fromUrl: (params) =>
      params.get(urlParamName) === "true"
        ? {
            type: type,
            value: true,
          }
        : undefined,
    toUrl: (value) => (value ? { [urlParamName]: "true" } : {}),
    title: (_) => title,
  };
}

function buildStringConfig(
  type: FilterType,
  urlParamName: string = type,
): FilterConfig<string | null> {
  return {
    type: type,
    fromUrl: (params) =>
      params.get(urlParamName)
        ? {
            type: type,
            value: params.get(urlParamName),
          }
        : undefined,
    toUrl: (value) => (value ? { [urlParamName]: value } : {}),
    title: (title: string | null) => {
      if (title === "ral_classic") {
        return "RAL Match";
      }
      if (title === "ral_design_plus") {
        return "RAL Design System plus";
      }
      if (title === "ncs") {
        return "NCS";
      }
      return title || "";
    },
  };
}

const oemColorToUrl = (
  oemColor: OemColorValue,
): Record<string, string | string[]> => ({
  ...(oemColor.library && { oem_library: oemColor.library }),
  ...(oemColor.code && { oem_code: oemColor.code }),
  ...(oemColor.name && { oem_color_name: oemColor.name }),
  ...(oemColor.match === "similar" && { oem_match: oemColor.match }),
  ...(oemColor.cieLab && {
    cie_lab: oemColor.cieLab.map((v) => v.toFixed(2)).join(","),
  }),
});

const oemColorFromUrl = (
  params: URLSearchParams,
): OemColorValue | undefined => {
  const library = params.get("oem_library") ?? undefined;
  const code = params.get("oem_code") ?? undefined;
  const name = params.get("oem_color_name") ?? undefined;
  const match = params.get("oem_match") === "similar" ? "similar" : "exact";
  const cieLab = params.get("cie_lab")?.split(",").map(parseFloat) as
    | ColorCieLab
    | undefined;
  if (!library && !code && !name && match === "exact") {
    return undefined;
  }
  return {
    ...(library && { library }),
    ...(code && { code }),
    ...(name && { name }),
    ...(cieLab && { cieLab }),
    match,
  };
};

export const filtersConfig: Record<FilterType, FilterConfig> = {
  color: {
    type: "color",
    toUrl: (filter: ColorFilterValue): Record<string, string | string[]> => {
      if (filter.oemColor) {
        return {
          colorSystem: filter.colorSystem,
          ...oemColorToUrl(filter.oemColor),
        };
      } else if (filter.color) {
        return {
          cie_lab: filter.color.value.map((v) => v.toFixed(2)).join(","),
          color_name: filter.color.name,
          colorSystem: filter.colorSystem,
        };
      } else if (filter.finalColorCode) {
        return {
          finalColorCode: filter.finalColorCode.map((it) => it.value),
          colorSystem: filter.colorSystem,
        };
      } else if (filter.yearOfDevelopment) {
        return {
          yearOfDevelopment: filter.yearOfDevelopment.map((it) => it.value),
          colorSystem: filter.colorSystem,
        };
      } else if (filter.preliminaryColorCode) {
        return {
          preliminaryColorCode: filter.preliminaryColorCode.map(
            (it) => it.value,
          ),
          colorSystem: filter.colorSystem,
        };
      } else {
        return {};
      }
    },
    fromUrl: (params) => {
      if (params.get("colorSystem") === OEM_COLOR_CODE_SYSTEM) {
        return {
          type: "color",
          value: {
            colorSystem: OEM_COLOR_CODE_SYSTEM,
            oemColor: oemColorFromUrl(params),
          },
        };
      }
      if (params.has("colorSystem")) {
        return {
          type: "color",
          value: {
            colorSystem: params.get("colorSystem"),
            color: params.has("cie_lab")
              ? {
                  name: params.get("color_name"),
                  value: params
                    .get("cie_lab")
                    ?.split(",")
                    .map((v) => parseFloat(v)),
                }
              : undefined,
            finalColorCode: params.has("finalColorCode")
              ? params
                  .getAll("finalColorCode")
                  .map(getFakeAutocompleteValue)
                  .filter((v) => v)
                  .map((v) => v!)
              : undefined,
            preliminaryColorCode: params.has("preliminaryColorCode")
              ? params
                  .getAll("preliminaryColorCode")
                  .map(getFakeAutocompleteValue)
                  .filter((v) => v)
                  .map((v) => v!)
              : undefined,
          },
        };
      }
    },
    title: (filter) => {
      if (filter.oemColor) {
        return oemColorTitle(filter.oemColor);
      } else if (filter.color) {
        if (filter.color.name === "CIE-Lab") {
          return `CIE-Lab: ${filter.color.value.map((v: number) => v.toFixed(2)).join(" ")}`;
        } else {
          return filter.color.name;
        }
      } else if (filter.finalColorCode) {
        return filter.finalColorCode
          .map((it: AutocompleteData) => it.name)
          .join(", ");
      } else if (filter.preliminaryColorCode) {
        return filter.preliminaryColorCode
          .map((it: AutocompleteData) => it.name)
          .join(", ");
      }
    },
  },
  colorGroup: buildAutocompleteConfig({
    type: "colorGroup",
    urlParamName: "colorGroup",
    lookupFunction: getColorGroupValue,
  }),
  opacity: buildAutocompleteConfig({
    type: "opacity",
    urlParamName: "opacity",
    lookupFunction: getOpacityValue,
  }),
  colorEffect: buildAutocompleteConfig({
    type: "colorEffect",
    urlParamName: "colorEffect",
    lookupFunction: getColorEffectValue,
  }),
  colorTag: {
    type: "colorTag",
    fromUrl: (params) =>
      params.has("colorTag")
        ? {
            type: "colorTag",
            value: params.getAll("colorTag").map((tag) => ({
              value: parseInt(tag.split(":")[0], 10),
              name: tag.split(":")[1],
            })),
          }
        : undefined,
    toUrl: (value) => ({
      colorTag: value.map(
        (tag: AutocompleteData) => `${tag.value}:${tag.name}`,
      ),
    }),
    title: (filter) =>
      filter.map((tag: AutocompleteData) => tag.name).join(", "),
  },
  grade: buildAutocompleteConfig({
    type: "grade",
    urlParamName: "grade",
    lookupFunction: getFakeAutocompleteValue,
  }),
  brand: buildAutocompleteConfig({
    type: "brand",
    urlParamName: "brand",
    lookupFunction: getFakeAutocompleteValue,
  }),
  gradeShortName: buildAutocompleteConfig({
    type: "gradeShortName",
    urlParamName: "gradeShortName",
    lookupFunction: getFakeAutocompleteValue,
  }),
  gradeNumber: buildAutocompleteConfig({
    type: "gradeNumber",
    urlParamName: "gradeNumber",
    lookupFunction: getFakeAutocompleteValue,
  }),
  gradeProperty: buildAutocompleteConfig({
    type: "gradeProperty",
    urlParamName: "gradeProperty",
    lookupFunction: getFakeAutocompleteValue,
  }),
  hasDigitalSample: buildBooleanConfig(
    "hasDigitalSample",
    "hasDigitalSample",
    "OMD Level 2",
  ),
  hasLevel3Data: buildBooleanConfig(
    "hasLevel3Data",
    "hasLevel3Data",
    "OMD Level 3",
  ),
  isColorSystem: buildStringConfig("isColorSystem", "isColorSystem"),
  colorSystemCode: buildStringConfig("colorSystemCode", "colorSystemCode"),
  colorStatus: buildAutocompleteConfig({
    type: "colorStatus",
    urlParamName: "status",
    lookupFunction: getColorStatusValue,
  }),
  substanceList: buildAutocompleteConfig({
    type: "substanceList",
    urlParamName: "sanctionList",
    lookupFunction: getFakeAutocompleteValue,
  }),
  cdc: buildAutocompleteConfig({
    type: "cdc",
    urlParamName: "cdc",
    lookupFunction: getFakeAutocompleteValue,
  }),
  oemName: buildAutocompleteConfig({
    type: "oemName",
    urlParamName: "oemName",
    lookupFunction: getFakeAutocompleteValue,
  }),
  customerName: buildAutocompleteConfig({
    type: "customerName",
    urlParamName: "customerName",
    lookupFunction: getFakeAutocompleteValue,
  }),
  uvp: buildAutocompleteConfig({
    type: "uvp",
    urlParamName: "uvp",
    lookupFunction: getFakeAutocompleteValue,
  }),
  industry: buildAutocompleteConfig({
    type: "industry",
    urlParamName: "industry",
    lookupFunction: getFakeAutocompleteValue,
  }),
  applicationClass: buildAutocompleteConfig({
    type: "applicationClass",
    urlParamName: "applicationClass",
    lookupFunction: getFakeAutocompleteValue,
  }),
  application: buildAutocompleteConfig({
    type: "application",
    urlParamName: "application",
    lookupFunction: getFakeAutocompleteValue,
  }),
  texture: buildAutocompleteConfig({
    type: "texture",
    urlParamName: "texture",
    lookupFunction: getFakeAutocompleteValue,
  }),
};
